import type { EventEmitter } from '@stencil/core';
import { Component, Host, h, Method, Prop, Element, Event } from '@stencil/core';
import type { AnchorInterface } from '../../utils/elements-interface';
import { generateAnchorAttributes, openURL } from '../../utils/elements-interface';

/**
 * @slot icon - Designed to receive tag's icon, placed on left side.
 * @slot  - Designed to receive tag's text content
 */

@Component({
  tag: 'sy-tag',
  styleUrl: 'scss/index.scss',
  shadow: true,
})
export class SyTag implements AnchorInterface {
  @Element() el: HTMLSyTagElement;

  /**
  HTML <a> download attribute
  */
  @Prop() download: string | undefined;

  /**
   * HTML <a> href attribute
   */
  @Prop({ reflect: true }) href: string | undefined;

  /**
   * HTML <a> rel attribute
   */
  @Prop() rel: string | undefined;

  /**
   * HTML <a> target attribute
   */
  @Prop() target: '_blank' | '_self' | '_parent' | '_top';

  /**
   * Sets tag's color based on system's colors
   */
  @Prop({ reflect: true }) color: 'primary' | 'secondary' | 'error' | 'warning' | 'success' | 'info';

  /**
   * If true, the tag can be dimissed by clicking in the close button.
   */
  @Prop({ reflect: true }) dismissible = false;

  /**
   * Sets tag's disabled state
   */
  @Prop({ reflect: true }) disabled = false;

  /**
   * Decrease its font size.
   */
  @Prop({ reflect: true }) small = false;

  /**
   * Emitted when the component dismiss
   */
  @Event() syTagDidDismiss: EventEmitter<void>;

  /**
   * Method to dismiss a tag. It will remove the tag from DOM.
   */
  @Method()
  async dismiss(): Promise<void> {
    if (this.dismissible) {
      this.syTagDidDismiss.emit();
      this.el.remove();
    }
  }

  private handleClick = (event: Event) => {
    if (!this.dismissible && this.href) {
      openURL(this.href, event);
    }
  };

  render() {
    return (
      <Host onClick={this.handleClick}>
        {!this.dismissible && this.href ? (
          <a
            class="sy-tag"
            {...generateAnchorAttributes(this)}
            {...(this.disabled ? { disabled: true, 'aria-disabled': 'true' } : null)}
          >
            <slot name="icon"></slot>
            <slot></slot>
          </a>
        ) : (
          <div class="sy-tag" {...(this.disabled ? { disabled: true } : null)}>
            <slot name="icon"></slot>
            <slot></slot>
            {this.dismissible && !this.disabled && (
              <sy-dismiss class="sy-dismiss" onClick={() => this.dismiss()}></sy-dismiss>
            )}
          </div>
        )}
      </Host>
    );
  }
}
